/**
 * @file LivePreview.tsx
 * @description Wrapper component that initializes Contentstack Live Preview functionality.
 * Enables real-time content editing and preview within the CMS interface.
 * Only activates when NEXT_PUBLIC_CONTENTSTACK_PREVIEW environment variable is set to 'true'.
 */

'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import ContentstackLivePreview from '@contentstack/live-preview-utils';

import { initLivePreview } from '@/lib/contentstack/live-preview';

/**
 * Props interface for LivePreview.
 */
interface LivePreviewProps {
  /** Child components to render */
  children?: React.ReactNode;
}

/**
 * Wrapper component that initializes Contentstack Live Preview SDK.
 * 
 * Live Preview allows content editors to see changes in real-time as they
 * edit content in the Contentstack CMS, without needing to publish or refresh.
 * 
 * Features:
 * - Conditionally initializes based on environment variable
 * - Runs once on component mount
 * - Transparent wrapper (renders children without modification)
 * - No visual UI (purely functional)
 * 
 * Environment Variables:
 * - `NEXT_PUBLIC_CONTENTSTACK_PREVIEW`: Set to 'true' to enable live preview
 * 
 * @param {LivePreviewProps} props - Component props with children
 * @returns {JSX.Element} Rendered children with live preview initialized
 * 
 * @example
 * ```tsx
 * // In root layout or page
 * <ContentstackLivePreview>
 *   <YourApp />
 * </ContentstackLivePreview>
 * ```
 */
export function LivePreview({ children }: LivePreviewProps) {
  const router = useRouter();
  // Check if live preview is enabled via environment variable
  const livePreviewEnabled = process.env.NEXT_PUBLIC_CONTENTSTACK_PREVIEW === 'true';

  /**
   * Initialize Contentstack Live Preview SDK on mount if enabled, then register
   * an onEntryChange callback that refreshes the Next.js server tree whenever the
   * editor saves a change in the CMS. Without this, SSR-rendered content stays
   * stale (the SDK has no built-in hook into Next.js routing) and the drift
   * between client state and server HTML can also surface as hydration warnings.
   *
   * `skipInitialRender: true` prevents the callback from firing once on mount,
   * which would trigger an unnecessary refresh on the first paint.
   */
  useEffect(() => {
    if (!livePreviewEnabled) return;
    initLivePreview();
    ContentstackLivePreview.onEntryChange(() => router.refresh(), {
      skipInitialRender: true,
    });
  }, [livePreviewEnabled, router]);

  // Render children without modification (transparent wrapper)
  return <>{children}</>;
}
